import { FormEntryValues } from '@app/api/routes/mce/forms.types';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { userScopedStorage } from './userScopedStorage';

type ListFormEntries = Record<number, FormEntryValues>;

export interface FormStore {
  listFormEntries: ListFormEntries;
  getFormEntryValues: (formId: number | undefined) => FormEntryValues;
  updateFieldValue: (formId: number, values: FormEntryValues) => void;
  clearFormEntry: (formId: number) => void;
  hasAnyFormFilling: () => boolean;
}

// Valeurs initiales réinitialisables (hors actions), partagées entre la création du store et resetFormEntriesStore.
const INITIAL_STATE = {
  listFormEntries: {}
} satisfies Pick<FormStore, 'listFormEntries'>;

export const useFormEntriesStore = create<FormStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...INITIAL_STATE,

        updateFieldValue: (formId: number, values: FormEntryValues) =>
          set((state) => {
            return {
              listFormEntries: {
                ...state.listFormEntries,
                [formId]: values
              }
            };
          }),

        clearFormEntry: (formId: number) =>
          set((state) => {
            const nextListFormEntries = { ...state.listFormEntries };
            delete nextListFormEntries[formId];
            return {
              listFormEntries: nextListFormEntries
            };
          }),

        getFormEntryValues: (formId: number | undefined) => {
          if (!formId) {
            return {};
          }
          return get().listFormEntries[formId] || {};
        },

        hasAnyFormFilling: () => {
          return Object.keys(get().listFormEntries).length > 0;
        }
      }),
      {
        // Clé distincte de `useFormStore` (qui utilise 'form-storage') pour
        // éviter la collision : les deux stores ont des schémas différents
        // (partialize côté useFormStore), un écrasait l'autre au moment du
        // persist setItem. Cf. CW-21966.
        name: 'form-entries-storage',
        storage: userScopedStorage<FormStore>(),
        skipHydration: true
      }
    ),
    {
      name: 'AppStore', // Nom affiché dans Redux DevTools
      enabled: true
    }
  )
);

/** Reset complet du store en mémoire (à appeler au logout). */
export const resetFormEntriesStore = () => {
  useFormEntriesStore.setState(INITIAL_STATE);
};